import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ExistingSupplierMatch, hasExactEmailMatch, matchReasonLabel } from 'src/app/shared/models/existing-supplier-match.model';

/** One selectable supplier, normalised from whichever source produced it. */
export interface PickableSupplier {
  supplierId: number;
  companyName: string;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  vendorCode: string | null;
  country: string | null;
  /** Where this row came from; a supplier can appear in more than one. */
  sources: Array<'ai' | 'mine' | 'mapped'>;
  /** "Already supplies this part": how many lines of this RFQ the supplier is linked to on MPS. */
  linesCovered?: number;
  /** Ranking hint from the suggestion endpoint, when it gave one. */
  score?: number | null;
  /** Already on the RFQ — shown as attached and not re-attachable. */
  alreadyAttached?: boolean;
}

type SourceKey = 'ai' | 'mine' | 'mapped';

/**
 * Choosing who gets asked to quote.
 *
 * An imported RFQ names the one supplier the document was addressed to, which
 * is rarely the whole answer — the buyer wants competing quotes. This offers
 * the two pools MPS already knows about and lets them pick before the RFQ
 * goes out:
 *
 *   AI suggested — MPS suppliers who actually list the parts on this RFQ
 *   My Suppliers — the buyer's own onboarded list
 *
 * Both pools are MPS suppliers, so there is no separate "global" list to wade
 * through: a supplier is either suggested because they carry these items, or
 * the buyer already knows them. Anyone else is onboarded deliberately.
 *
 * Selection is by supplier id, so the same company appearing in two pools can
 * only ever be attached once.
 */
@Component({
  selector: 'app-supplier-picker',
  templateUrl: './supplier-picker.component.html',
  styleUrls: ['./supplier-picker.component.css'],
})
export class SupplierPickerComponent {
  @Input() open = false;
  @Input() loading = false;
  @Input() rfqRefNumber: string | null = null;
  @Input() error = '';

  /** Normalised, already de-duplicated across sources by the caller. */
  @Input() suppliers: PickableSupplier[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<PickableSupplier[]>();

  /** Asked for when neither pool has the company the buyer wants. */
  @Output() onboard = new EventEmitter<void>();

  /**
   * 2026-10-20: MPS suppliers that look like the company being onboarded. While set, the picker offers "Link" for
   * each and "create new" only behind an explicit confirm.
   */
  @Input() existingMatches: ExistingSupplierMatch[] = [];
  @Output() linkExisting = new EventEmitter<ExistingSupplierMatch>();
  @Output() createNewAnyway = new EventEmitter<void>();
  confirmingCreate = false;
  reasonLabel = matchReasonLabel;
  /** No "create new" when the e-mail already belongs to a supplier on MPS (a second account cannot use it). */
  get allowCreateNew(): boolean {
    return !hasExactEmailMatch(this.existingMatches);
  }

  confirmCreateNew(): void {
    this.confirmingCreate = false;
    this.createNewAnyway.emit();
  }

  trackByMatch(_i: number, m: ExistingSupplierMatch): number {
    return m.supplierId;
  }

  activeSource: SourceKey = 'ai';
  search = '';

  /** Chosen supplier ids. A Set is the point: one company, one attachment. */
  private chosen = new Set<number>();

  /** Shown when the buyer tries to attach nothing. */
  notice: { title: string; detail: string } | null = null;

  readonly tabs: Array<{ key: SourceKey; label: string; hint: string }> = [
    { key: 'ai', label: 'Suggested', hint: 'MPS suppliers who list the parts on this RFQ' },
    { key: 'mine', label: 'My Suppliers', hint: 'Suppliers you have onboarded' },
    { key: 'mapped', label: 'Already supplies this part', hint: 'MPS suppliers already linked to the same IMPA, ISSA or part number' },
  ];

  countFor(source: SourceKey): number {
    return (this.suppliers || []).filter((s) => s.sources.includes(source)).length;
  }

  get visible(): PickableSupplier[] {
    const term = this.search.trim().toLowerCase();
    return (this.suppliers || [])
      .filter((s) => s.sources.includes(this.activeSource))
      .filter((s) =>
        !term ||
        [s.companyName, s.contactPerson, s.email, s.vendorCode, s.country]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term))
      );
  }

  get chosenCount(): number {
    return this.chosen.size;
  }

  /** Everything picked, across every tab — not just what is on screen. */
  get chosenSuppliers(): PickableSupplier[] {
    return (this.suppliers || []).filter((s) => this.chosen.has(s.supplierId));
  }

  isChosen(supplier: PickableSupplier): boolean {
    return this.chosen.has(supplier.supplierId);
  }

  toggle(supplier: PickableSupplier): void {
    if (supplier.alreadyAttached) return;
    if (this.chosen.has(supplier.supplierId)) {
      this.chosen.delete(supplier.supplierId);
    } else {
      this.chosen.add(supplier.supplierId);
    }
    this.notice = null;
  }

  /** Selects everything currently listed; already-attached rows are skipped. */
  selectAllVisible(): void {
    this.visible.forEach((s) => {
      if (!s.alreadyAttached) this.chosen.add(s.supplierId);
    });
    this.notice = null;
  }

  clearSelection(): void {
    this.chosen.clear();
  }

  initials(supplier: PickableSupplier): string {
    const source = (supplier?.companyName || '').trim();
    if (!source) return '?';
    const words = source.split(/\s+/).filter(Boolean);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }

  sourceLabel(key: SourceKey): string {
    return this.tabs.find((t) => t.key === key)?.label || key;
  }

  attach(): void {
    if (!this.chosen.size) {
      // Said in the panel rather than as a toast: it is a precondition for the
      // button they just pressed, so it belongs next to it.
      this.notice = {
        title: 'Choose at least one supplier',
        detail:
          'An RFQ has to be addressed to someone before it can go out. Pick from the ' +
          'suggestions or your own suppliers — or onboard the company you want to ask.',
      };
      return;
    }
    this.confirm.emit(this.chosenSuppliers);
  }

  closePanel(): void {
    this.notice = null;
    this.search = '';
    this.close.emit();
  }

  trackBySupplier(_i: number, s: PickableSupplier): number {
    return s.supplierId;
  }
}
